/**
 * The home screen: where you stand, with whom, and what has just happened.
 *
 * Four things, in the order somebody wants them: the one number that answers
 * "am I up or down", the two numbers it is made of, the people it is made of,
 * and then the feed. Every one of them is labelled in words — `Amount` is the
 * only thing on this screen that renders a balance, and it will not render
 * one without saying which way it points.
 *
 * The data is `GET /api/balances` (people and totals), `GET /api/groups` and
 * `GET /api/activity`. The totals are the server's, over every counterparty
 * rather than the loaded page's, and they are only ever recomputed here while
 * an optimistic write is in flight; see `src/lib/optimistic.ts`.
 *
 * **It is also the only door into the app.** Every other feature there is —
 * a split, a receipt, settling up, inviting somebody — lives under
 * `/groups/:groupId`, and an account nobody has sent an invite link to has no
 * group to open. So "Your groups" carries a `New group` control that is
 * always there, and, for somebody with no groups at all, a first-run call to
 * action in its place. Making one navigates straight into it, because the
 * next thing that person wants is the expense.
 *
 * **And the expense itself is here, not a screen away.** Filing one used to
 * mean picking the group first, every time, and it is the same group most
 * days — so the sheet is on this screen, under the number it is about to
 * move, already pointed at the group and category this person filed last.
 * `src/lib/last-expense.ts` is the rule about where that guess comes from and
 * what happens when there is nothing to go on; `HomeExpense` below is the
 * sheet once a group has been settled on. Everything in it stays editable and
 * Reset throws the guess away, because a pre-fill that cannot be argued with
 * is how an expense quietly lands in the wrong group.
 */

import { useCallback, useMemo, useState, type FormEvent } from 'react'
import { describeNet, describePerson, formatMoney } from '../lib/format'
import { MAX_GROUP_NAME, readGroupDraft, suggestedCurrency } from '../lib/group-draft'
import {
  blankExpense,
  filableGroups,
  suggestedExpense,
  type LastExpense,
} from '../lib/last-expense'
import { lastExpenseFiled } from '../last-expense'
import { Amount } from '../components/Amount'
import { Avatar } from '../components/Avatar'
import { Confetti } from '../components/Confetti'
import { PushCard } from '../components/PushCard'
import { staggerStyle, useCountingMoney } from '../motion-react'
import { useActivity, useBalances, useCreateGroup, useGroup, useGroups } from '../queries'
import type { Route } from '../routes'
import { ActivityFeed } from './Activity'
import { AddExpense } from './AddExpense'

/**
 * There is no currency on `/api/balances`, deliberately: it nets across
 * groups, and this codebase does not convert between currencies. Until a
 * person can be in groups of two different currencies and have that netting
 * mean something, the home screen states the one its groups are in — and says
 * so plainly rather than picking silently — falling back to USD when there is
 * nothing to go on.
 */
function homeCurrency(currencies: readonly string[]): { code: string; mixed: boolean } {
  const distinct = [...new Set(currencies)]
  if (distinct.length === 0) return { code: 'USD', mixed: false }
  return { code: distinct[0], mixed: distinct.length > 1 }
}

/**
 * Two fields: what the group is for, and which currency it counts in.
 *
 * The currency is asked for here rather than left to the server's USD default
 * and fixed later, because it cannot always be fixed later: `PATCH
 * /api/groups/:id` refuses a currency change once the group has any
 * transactions, which for most groups is about a minute after this form is
 * submitted. It is pre-filled with `suggestedCurrency` — whatever this
 * person's existing groups agree on — so the common case is still one field.
 *
 * `readGroupDraft` is the rule, and `src/lib/group-draft.ts` is where it
 * lives. The server checks the same two things again and is the authority;
 * this only means somebody hears about a fourth letter in the currency box
 * while the box is still in front of them.
 */
function NewGroupForm({
  suggested,
  onMade,
  onCancel,
}: {
  suggested: string
  onMade: (groupId: string) => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [currency, setCurrency] = useState(suggested)
  const [problem, setProblem] = useState<string | null>(null)
  const createGroup = useCreateGroup()

  const submit = useCallback(
    (event: FormEvent) => {
      event.preventDefault()
      const reading = readGroupDraft(name, currency)
      if (!reading.ok) {
        setProblem(reading.problem)
        return
      }
      setProblem(null)
      createGroup.mutate(reading.draft, { onSuccess: (made) => onMade(made.group.id) })
    },
    [createGroup, currency, name, onMade],
  )

  return (
    <form className="form" onSubmit={submit}>
      <div className="form__pair">
        <p className="form__row">
          <label htmlFor="new-group-name">What is it for?</label>
          <input
            id="new-group-name"
            name="name"
            autoFocus
            maxLength={MAX_GROUP_NAME}
            placeholder="Cabin weekend"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </p>
        <p className="form__row">
          <label htmlFor="new-group-currency">Currency</label>
          <input
            id="new-group-currency"
            name="currency"
            maxLength={3}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            value={currency}
            onChange={(event) => setCurrency(event.target.value.toUpperCase())}
          />
        </p>
      </div>

      <p className="muted note">
        Everything in this group is counted in that currency, and it cannot be changed once there
        is an expense in it. You can add people once you are inside — by name, or with a link.
      </p>

      <p className="actions">
        <button type="submit" disabled={createGroup.isPending || name.trim().length === 0}>
          {createGroup.isPending ? 'Making it…' : 'Make the group'}
        </button>
        <button
          type="button"
          className="button--quiet"
          disabled={createGroup.isPending}
          onClick={onCancel}
        >
          Cancel
        </button>
      </p>

      {problem && <p className="bad note">{problem}</p>}
      {createGroup.isError && <p className="bad note">{createGroup.error.message}</p>}
    </form>
  )
}

/**
 * The sheet itself, once a group has been settled on.
 *
 * Its own component because of the request: the expense sheet needs the
 * group's members — who it is split between, and in the order an odd cent is
 * broken by — and `GET /api/groups/:id` is the only place that list comes
 * from. A hook cannot be called for a group that has not been chosen yet, so
 * choosing is the parent's job and this is only ever rendered with an answer.
 * It is the same query the group's own screen makes, so walking in there
 * afterwards costs nothing.
 *
 * `AddExpense` is unchanged in every other respect: the same sheet, the same
 * optimistic write, the same folded payer and split editors. What the home
 * screen adds is which group it points at and what it opens on.
 */
function HomeExpense({
  groupId,
  category,
  myId,
  onCleared,
  onDone,
}: {
  groupId: string
  /** What the category row opens on — see `src/lib/last-expense.ts`. */
  category: string | null
  myId: string
  /** Pressing Clear, which empties the sheet and leaves the group alone. */
  onCleared: () => void
  onDone: () => void
}) {
  const group = useGroup(groupId)

  if (group.isPending) return <p className="muted">Fetching who is in it…</p>
  if (group.isError) return <p className="bad">{group.error.message}</p>
  if (!group.data) return null

  const members = group.data.members
  if (members.every((member) => member.leftAt !== null)) {
    return (
      <p className="muted">Everybody has left that group, so there is nobody to owe anything.</p>
    )
  }

  return (
    <AddExpense
      groupId={groupId}
      currency={group.data.group.currency}
      myId={myId}
      members={members}
      category={category}
      dismissLabel="Clear"
      onDismiss={onCleared}
      onDone={onDone}
    />
  )
}

export function Home({ myId, navigate }: { myId: string; navigate: (to: Route) => void }) {
  const balances = useBalances()
  const groups = useGroups()
  const activity = useActivity()

  /** Whether the "New group" form is open. Closed until somebody asks for it. */
  const [making, setMaking] = useState(false)

  /**
   * What this browser filed last, read once on the way in — the group and the
   * category the composer below opens on. It is state rather than a bare read
   * so that filing an expense here re-reads it: `AddExpense` writes it as the
   * write goes out, so the next sheet opens on what was just filed even when
   * that was a different group from the one this screen loaded with.
   */
  const [remembered, setRemembered] = useState<LastExpense | null>(() => lastExpenseFiled())
  /**
   * The group and category somebody has *chosen*, which is not the same thing
   * as the one suggested to them. `null` means they have not chosen: follow
   * the suggestion, and keep following it as the two requests it is made of
   * arrive. `{ value: null }` is Reset — a deliberate nothing, which has to
   * outlast a suggestion that would otherwise fill the sheet straight back in.
   */
  const [chosen, setChosen] = useState<{ value: LastExpense | null } | null>(null)
  /**
   * What makes the composer a fresh sheet again, the way the group screen's
   * composer does it: filing is optimistic, so there is no panel to close and
   * the sheet is re-mounted instead. It changes on a filing and on a Reset,
   * and the group id is in the key as well — a sheet holds a split between
   * *these* people, and carrying it over to another group would be carrying a
   * list of participants who are not in it.
   */
  const [composer, setComposer] = useState(0)

  /**
   * Which group the composer offers, and which one it opens on.
   *
   * Both decisions are `src/lib/last-expense.ts`: what may be filed into, and
   * — from what this browser filed last, then from the feed already on this
   * screen, then from there being only one group — which of them the sheet
   * should be pointed at before anybody touches it.
   */
  const choosable = useMemo(
    () => filableGroups(groups.data?.groups ?? []),
    [groups.data?.groups],
  )
  const suggested = useMemo(
    () =>
      suggestedExpense({
        remembered,
        groups: groups.data?.groups ?? [],
        feed: activity.data?.activity ?? [],
      }),
    [remembered, groups.data?.groups, activity.data?.activity],
  )
  /**
   * A choice outlives the request that would confirm it, but not the group
   * going away underneath it: somebody else archiving the group this is
   * pointed at would otherwise leave the picker showing one thing and the
   * sheet filing into another, so an unofferable choice falls back to the
   * suggestion exactly as if it had never been made.
   */
  const prefill = ((): LastExpense | null => {
    if (chosen === null) return suggested
    if (chosen.value === null) return null
    return choosable.some((group) => group.id === chosen.value?.groupId)
      ? chosen.value
      : suggested
  })()
  /**
   * Whether Reset has anything to undo: a category to clear, or a group to
   * un-choose. With one group and no category there is nothing pre-filled to
   * start from — the sheet's own Clear is what empties what has been typed —
   * so the control is not drawn rather than drawn doing nothing.
   */
  const resettable =
    prefill !== null && (prefill.category !== null || choosable.length > 1)

  const groupCurrencies = (groups.data?.groups ?? []).map((group) => group.currency)
  const currency = homeCurrency(groupCurrencies)
  const totals = balances.data?.totals
  const people = balances.data?.people ?? []
  const net = describeNet(totals?.netCents ?? 0, currency.code)

  /**
   * The three numbers at the top of the screen count to their values rather
   * than appearing at them — from nought on the first paint, and from the old
   * figure every time a write moves them. The headline gets the celebration as
   * well: somebody whose net position *arrives* at zero has just settled up
   * with everybody they know, which is the single best thing that can happen
   * on this screen.
   */
  const owedToYou = totals?.owedToYouCents ?? 0
  const youOwe = totals?.youOweCents ?? 0
  const headline = useCountingMoney({
    cents: net.cents,
    currency: currency.code,
    final: net.amount,
  })
  const owed = useCountingMoney({
    cents: owedToYou,
    currency: currency.code,
    final: formatMoney(owedToYou, currency.code),
  })
  const owe = useCountingMoney({
    cents: youOwe,
    currency: currency.code,
    final: formatMoney(youOwe, currency.code),
  })

  return (
    <>
      <section
        className={
          `card position position--${net.direction}` +
          (headline.settling ? ' position--settling' : '')
        }
      >
        <h2>Where you stand</h2>

        {balances.isPending && <p className="muted">Adding it all up…</p>}
        {balances.isError && <p className="bad">{(balances.error as Error).message}</p>}

        {balances.data && (
          <>
            <p className="position__amount">
              <span className="position__value" ref={headline.ref}>
                {net.amount}
              </span>
              <span className="position__words">{net.headline}</span>
              {headline.settling && <Confetti />}
            </p>

            <dl className="split">
              <div className="split__half split__half--incoming">
                <dt>Owed to you</dt>
                <dd ref={owed.ref}>{formatMoney(owedToYou, currency.code)}</dd>
              </div>
              <div className="split__half split__half--outgoing">
                <dt>You owe</dt>
                <dd ref={owe.ref}>{formatMoney(youOwe, currency.code)}</dd>
              </div>
            </dl>

            {currency.mixed && (
              <p className="muted note">
                Your groups are not all in the same currency. These figures add the amounts
                together without converting them, so read them per group below.
              </p>
            )}
          </>
        )}
      </section>

      {/*
        The expense sheet, on the home screen, second only to the number it is
        about to move.

        This is the entry point the app did not have. Filing an expense lived
        under `/groups/:groupId` and nowhere else, so the shortest path to it
        was: open the app, read the home screen, remember which group the
        coffee belongs in, find it in the list below, wait for that screen,
        and only then type. Every one of those steps is the same answer every
        day, which is what makes them worth pre-filling rather than asking
        again — so the sheet opens on the group and the category this person
        filed last and the whole of it is the amount.

        Nothing is fixed by being pre-filled. The group is a control directly
        above the sheet, the category a row inside it, and the people, the
        date and the split are exactly where they were; Reset drops the
        pre-fill entirely for the day the last expense is a bad guess. It is
        not drawn at all for somebody with no group to file into — that person
        wants the call to action further down, not a sheet with nowhere to
        send it.
      */}
      {choosable.length > 0 && (
        <section className="card">
          <div className="card__head">
            <h2>Add an expense</h2>
            {resettable && (
              <button
                type="button"
                className="button--quiet"
                onClick={() => {
                  setChosen({ value: blankExpense(groups.data?.groups ?? []) })
                  setComposer((count) => count + 1)
                }}
              >
                Reset
                <span className="visually-hidden">
                  {' '}
                  — clear the group and category this sheet is filled in with
                </span>
              </button>
            )}
          </div>

          <div className="form__row form__row--narrow">
            <label htmlFor="home-expense-group">Which group?</label>
            <select
              id="home-expense-group"
              value={prefill?.groupId ?? ''}
              onChange={(event) => {
                const groupId = event.target.value
                setChosen({
                  value: groupId === '' ? null : { groupId, category: prefill?.category ?? null },
                })
                setComposer((count) => count + 1)
              }}
            >
              {prefill === null && <option value="">Choose one…</option>}
              {choosable.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name} · {group.currency}
                </option>
              ))}
            </select>
          </div>

          {prefill === null ? (
            <p className="muted note">
              Pick the group this expense belongs in and the sheet opens here. Whichever you file
              into is the one it will open on next time.
            </p>
          ) : (
            <>
              {/*
                Said only when the pre-fill really is the last expense — not
                when it came from the feed or from there being one group,
                which are guesses about somebody rather than a record of them.
                It is also where Reset gets explained, since a control that
                only matters on the day the guess is wrong is one nobody goes
                looking for.
              */}
              {chosen === null && remembered?.groupId === prefill.groupId && (
                <p className="muted note">
                  Filled in from your last expense. Change anything you like — or press Reset for
                  a blank sheet.
                </p>
              )}
              <HomeExpense
                key={`${prefill.groupId}-${composer}`}
                groupId={prefill.groupId}
                category={prefill.category}
                myId={myId}
                // Clear empties what has been typed and nothing else: the
                // group above the sheet is a choice, not part of the draft.
                onCleared={() => setComposer((count) => count + 1)}
                onDone={() => {
                  // Whatever has just been filed is what the next sheet should
                  // open on, and `AddExpense` has already written it down — so
                  // this re-reads that rather than being told, which is also
                  // what makes pressing Clear leave the pre-fill alone.
                  setRemembered(lastExpenseFiled())
                  setChosen(null)
                  setComposer((count) => count + 1)
                }}
              />
            </>
          )}
        </section>
      )}

      {/*
        Notifications, offered once, here rather than in a settings screen
        nobody visits: the home screen is the one place everybody passes
        through, and it draws nothing of its own accord for a browser that
        cannot do this — see `PushCard`.
      */}
      <PushCard />

      <section className="card">
        <h2>People</h2>
        {balances.data && people.length === 0 && (
          <p className="muted">Nobody owes anybody anything. Add an expense to a group to start.</p>
        )}
        <ul className="people">
          {people.map((person, index) => {
            const said = describePerson(person.displayName, person.amountCents, currency.code)
            return (
              <li key={person.userId} className="person" style={staggerStyle(index)}>
                <Avatar userId={person.userId} displayName={person.displayName} />
                <div className="person__who">
                  <p className="person__name">{person.displayName}</p>
                  <p className="person__groups">
                    {person.groups.map((share) => (
                      <button
                        key={share.groupId}
                        type="button"
                        className="tag"
                        onClick={() => navigate({ name: 'group', groupId: share.groupId })}
                      >
                        {share.groupName}
                        <span className="tag__amount">
                          {share.amountCents === 0
                            ? 'settled'
                            : `${share.amountCents > 0 ? 'owes you' : 'you owe'} ${formatMoney(share.amountCents, currency.code)}`}
                        </span>
                      </button>
                    ))}
                  </p>
                </div>
                <Amount
                  direction={said.direction}
                  label={said.label}
                  cents={said.cents}
                  currency={currency.code}
                />
                <span className="visually-hidden">{said.sentence}</span>
              </li>
            )
          })}
        </ul>
      </section>

      <section className="card">
        <div className="card__head">
          <h2>Your groups</h2>
          {/*
            Persistent, not only shown when the list is empty: a second group
            is as ordinary a thing to want as a first, and there is nowhere
            else in the app to ask for one.
          */}
          <button type="button" className="button--quiet" onClick={() => setMaking(!making)}>
            {making ? 'Cancel' : 'New group'}
          </button>
        </div>

        {making && (
          <NewGroupForm
            suggested={suggestedCurrency(groupCurrencies)}
            onMade={(groupId) => navigate({ name: 'group', groupId })}
            onCancel={() => setMaking(false)}
          />
        )}

        {groups.isPending && <p className="muted">Looking…</p>}
        {groups.isError && <p className="bad">{(groups.error as Error).message}</p>}
        {groups.data?.groups.length === 0 && !making && (
          <div className="empty">
            <p>You are not in a group yet.</p>
            <p className="muted">
              A group is where an expense goes — a trip, a flat, the two of you. Make one and you
              can add the first expense straight away; the people can come after, by name or with
              a link.
            </p>
            <p className="actions">
              <button type="button" onClick={() => setMaking(true)}>
                Make your first group
              </button>
            </p>
          </div>
        )}
        <ul className="groups">
          {groups.data?.groups.map((group, index) => (
            <li key={group.id} style={staggerStyle(index)}>
              <button
                type="button"
                className="group-link"
                onClick={() => navigate({ name: 'group', groupId: group.id })}
              >
                <span className="group-link__name">{group.name}</span>
                <span className="group-link__meta">
                  {group.memberCount} {group.memberCount === 1 ? 'person' : 'people'} ·{' '}
                  {group.currency}
                  {group.archivedAt !== null && ' · archived'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="card">
        <h2>Recent activity</h2>
        <ActivityFeed
          entries={activity.data?.activity ?? []}
          isPending={activity.isPending}
          isError={activity.isError}
          error={activity.error}
          onOpenGroup={(groupId) => navigate({ name: 'group', groupId })}
        />
      </section>
    </>
  )
}
